import React, { useState, useMemo, useCallback, useContext } from 'react';
import { Box, Button, Typography } from '@mui/material';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { trackEvent, getWindow } from '@surfline/web-common';
import type { Units } from 'types/units';

import MissingFavourites from 'components/MissingFavourites';
import VideoPlayer from 'components/VideoPlayer';
import { SL_WEB_ANON_YOUTUBE_HOMEPAGE } from 'common/treatments';
import config from 'config';
import { PageContext } from 'contexts/PageContext';
import {
  useMaxWidthDesktop,
  useMaxWidthTablet,
  useMinWidthMobileLarge,
} from 'hooks/useMediaQueries';

import { useTreatments } from 'utils/treatments';
import { mapPaths } from 'utils/urls/mapPaths';
import FavoritesCarouselSnap from 'components/FavoritesCarouselSnap';
import { DEFAULT_UNITS } from 'common/constants';
import type { UserFavoritesResponse } from 'types/userFavorites';
import type { SpotRecommendation } from 'common/api/spotRecommendations';
import { AnonHeroLogo, AnonHeroBackground } from './AnonHeroSVGs';
import useAnonymousHeroColorScheme from './useAnonymousHeroColorScheme';

import styles from './AnonHero.module.scss';

const ANON_HERO_LIVE_STREAM_URL = 'https://hls.cdn-surfline.com/ohio/sltv-global/playlist.m3u8';
const ROTATING_CAM_AUTO_PAUSE_SECONDS = 10 * 60;

interface AnonHeroProps {
  geoTarget: string;
  colorSchemeIndex?: number;
  units: Units;
  spotsResponse: UserFavoritesResponse | undefined;
  error: Error | undefined;
  isLoadingRecommendations?: boolean;
  recommendations?: Array<SpotRecommendation>;
  onReachingEnd?: () => void;
  onRecommendationFavorited?: (spotId: string) => void;
  onRecommendationImpression?: (recommendation: SpotRecommendation, index: number) => void;
}

const AnonHero: React.FC<AnonHeroProps> = ({
  geoTarget,
  colorSchemeIndex,
  units,
  spotsResponse,
  error,
  isLoadingRecommendations,
  recommendations,
  onReachingEnd,
  onRecommendationFavorited,
  onRecommendationImpression,
}) => {
  const isMobileLarge = useMinWidthMobileLarge();
  const isDesktop = !useMaxWidthTablet();
  const isDesktopLarger = !useMaxWidthDesktop();
  const window = getWindow();
  const { heroColorScheme: colorScheme } = useAnonymousHeroColorScheme(colorSchemeIndex);
  const { pageName } = useContext(PageContext);
  const { asPath } = useRouter();
  const treatments = useTreatments();
  const [useFallback, setUseFallback] = useState(false);

  // Note: we've replaced the YouTube embed with our own hosted stream version
  // of rotating cams but we've kept the legacy treatment name.
  const showRotatingCamStream = treatments?.[SL_WEB_ANON_YOUTUBE_HOMEPAGE] === 'on' && !useFallback;

  const ctaText = 'Get started';
  const subHeadline = 'Cams. Forecasts. Reports. Travel. Stories.';
  const headline = 'All things surf, all in one place.';

  const videoInfoFromGeo = useMemo(() => {
    let videoSize = '360p';
    if (isMobileLarge) videoSize = '720p';
    if (isDesktopLarger) videoSize = '1080p';

    const heroVideos = {
      US: {
        poster: `${config.cdnUrl}/kbyg/videos/thumbnails/surfline-cams-us.jpg`,
        videos: [
          {
            src: `${config.cdnUrl}/kbyg/videos/surfline-cams-us-${videoSize}.mp4`,
            type: 'video/mp4',
          },
        ],
      },
      ANZ: {
        poster: `${config.cdnUrl}/kbyg/videos/thumbnails/surfline-cams-au.jpg`,
        videos: [
          {
            src: `${config.cdnUrl}/kbyg/videos/surfline-cams-au-${videoSize}.mp4`,
            type: 'video/mp4',
          },
        ],
      },
      EU: {
        poster: `${config.cdnUrl}/kbyg/videos/thumbnails/surfline-cams-eu.jpg`,
        videos: [
          {
            src: `${config.cdnUrl}/kbyg/videos/surfline-cams-eu-${videoSize}.mp4`,
            type: 'video/mp4',
          },
        ],
      },
      DEFAULT: {
        poster: `${config.cdnUrl}/kbyg/videos/thumbnails/surfline-cams.jpg`,
        videos: [
          {
            src: `${config.cdnUrl}/kbyg/videos/surfline-cams-${videoSize}.mp4`,
            type: 'video/mp4',
          },
        ],
      },
    };

    if (geoTarget === 'US') return heroVideos.US;
    if (geoTarget === 'EU') return heroVideos.EU;
    if (geoTarget === 'AU' || geoTarget === 'NZ') return heroVideos.ANZ;

    return heroVideos.DEFAULT;
  }, [geoTarget, isDesktopLarger, isMobileLarge]);

  const heroVideo = useMemo(() => {
    if (showRotatingCamStream) {
      return {
        poster: '',
        sources: [{ src: ANON_HERO_LIVE_STREAM_URL, type: 'application/x-mpegURL' }],
        loop: false,
      };
    }

    return {
      poster: videoInfoFromGeo.poster,
      sources: videoInfoFromGeo.videos,
      loop: true,
    };
  }, [showRotatingCamStream, videoInfoFromGeo]);

  const headerVariants = useMemo(() => {
    if (isDesktopLarger) return 'subHeadline';
    if (isDesktop) return 'callout1';

    return 'subHeadline';
  }, [isDesktop, isDesktopLarger]);

  const defaultMissingArgs = {
    title: 'Favorites',
    boldText: 'Welcome to Surfline',
    normalText: 'Lets get started finding you some waves.',
  };

  const isLoadingRecs = isLoadingRecommendations && !recommendations?.length;
  const getCarouselTitle = () => {
    if (isLoadingRecs || recommendations?.length) return 'Recommended spots for you';
    return isDesktop ? 'View live surf reports and surf forecasts' : 'Live surf reports';
  };
  const carouselTitle = getCarouselTitle();

  const callToActionButtonText = isDesktop ? 'Check the surf' : 'View all';

  const callToActionButtonProps = useMemo(
    () => ({
      link: mapPaths.base,
      text: callToActionButtonText,
      trackingEvent: {
        event: 'Button Clicked',
        properties: {
          buttonText: callToActionButtonText,
          pageName,
          uiElement: 'Home hero',
        },
      },
    }),
    [callToActionButtonText, pageName],
  );

  const nextTracking = useMemo(
    () => ({
      event: 'Button Clicked',
      properties: {
        buttonText: 'Right Scroll',
        pageName: 'Home',
        buttonLocation: 'Favorites Carousel',
        buttonName: 'Right Scroll',
        path: asPath,
        url: `${window?.location.host}${asPath}`,
      },
    }),
    [asPath, window?.location.host],
  );

  const prevTracking = useMemo(
    () => ({
      event: 'Button Clicked',
      properties: {
        buttonText: 'Left Scroll',
        pageName: 'Home',
        buttonLocation: 'Favorites Carousel',
        buttonName: 'Left Scroll',
        path: asPath,
        url: `${window?.location.host}${asPath}`,
      },
    }),
    [asPath, window?.location.host],
  );

  const onClickGetStarted = useCallback(() => {
    trackEvent('Clicked CTA', {
      location: 'Home Hero',
      pageName,
      ctaText,
      category: 'subscription',
      path: asPath,
      url: `${window?.location.host}${asPath}`,
    });
  }, [pageName, asPath, window?.location.host]);

  const onStreamError = useCallback(() => setUseFallback(true), []);

  const carouselFavorites = useMemo(
    () => (isLoadingRecs || recommendations?.length ? [] : (spotsResponse?.data?.favorites ?? [])),
    [isLoadingRecs, recommendations?.length, spotsResponse?.data?.favorites],
  );

  const showMissingFavorites =
    spotsResponse?.data?.favorites.length === 0 && !recommendations?.length && !error;
  const showCarousel = !!(
    recommendations?.length ||
    spotsResponse?.data?.favorites ||
    isLoadingRecs
  );

  return (
    <Box
      className={classNames(styles.anonHeroWrapper, 'sl-section-full-bleed')}
      data-testid="anonymous-home-page-module"
      style={{ background: colorScheme?.background }}
    >
      <AnonHeroBackground
        fill={colorScheme.details}
        dataTest={`anon-hero-bg-${colorScheme.name}`}
      />
      <Box className={classNames(styles.anonHeroContent, 'sl-section-container')}>
        <Box className={styles.anonHeroVideoSection}>
          <Box className={styles.anonHeroVideoWrapper}>
            <VideoPlayer
              autoPauseAfterSeconds={
                showRotatingCamStream ? ROTATING_CAM_AUTO_PAUSE_SECONDS : undefined
              }
              autoplay
              enableClickToTogglePlayback={showRotatingCamStream}
              id="anonymous-home-page-video-player"
              loop={heroVideo.loop}
              muted
              onError={showRotatingCamStream ? onStreamError : undefined}
              poster={heroVideo.poster}
              skin="none"
              sources={heroVideo.sources}
              testId="anonymous-home-page-video-player"
            />
          </Box>
        </Box>
        <Box className={styles.anonHeaderSection}>
          <AnonHeroLogo
            fill={colorScheme?.details}
            dataTest={`anon-hero-logo-${colorScheme.name}`}
          />
          <Typography
            className={styles.anonHeaderHeadline}
            color={colorScheme?.details}
            component="h1"
            variant="headline"
          >
            {headline}
          </Typography>
          <Typography
            className={styles.anonHeroSubheadline}
            color={colorScheme?.textColor}
            variant={headerVariants}
            component="h2"
          >
            {subHeadline}
          </Typography>
          <Box className={styles.anonHeaderButtonWrapper}>
            <Button
              data-testid="get-started-cta"
              fullWidth={false}
              href="/create-account"
              onClick={onClickGetStarted}
              size={isDesktopLarger ? 'large' : 'medium'}
              sx={{
                boxShadow: 0,
                backgroundColor: colorScheme.buttonBackground,
                color: colorScheme.buttonText,
                '&:hover,&:focus-visible': {
                  color: colorScheme.buttonBackground,
                  backgroundColor: colorScheme.buttonText,
                  boxShadow: 0,
                },
              }}
              variant="secondary"
            >
              {ctaText}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box className={styles.anonHeroMobileDivider} />
      <Box className={styles.anonHeroCarouselWrapper} data-testid="anon-hero-carousel-wrapper">
        {showMissingFavorites && (
          <section className="sl-section-container">
            <MissingFavourites
              title={defaultMissingArgs.title}
              boldText={defaultMissingArgs.boldText}
              normalText={defaultMissingArgs.normalText}
            />
          </section>
        )}
        {showCarousel && (
          <FavoritesCarouselSnap
            callToActionButton={callToActionButtonProps}
            favorites={carouselFavorites}
            isActive={false}
            isLoadingRecommendations={isLoadingRecommendations}
            mode="dark"
            nextTracking={nextTracking}
            prevTracking={prevTracking}
            title={carouselTitle}
            units={units ?? DEFAULT_UNITS}
            recommendations={recommendations}
            onReachingEnd={onReachingEnd}
            onRecommendationFavorited={onRecommendationFavorited}
            onRecommendationImpression={onRecommendationImpression}
          />
        )}
      </Box>
    </Box>
  );
};

export default AnonHero;
